<template>
  <!-- eslint-disable max-len -->
  <div class="game-entry-root">
    <div v-if="isTemporary" class="game-box-temp-badge">
      <game-expiration-badge
        :temporary="isTemporary"
        :expired="isExpired"
        :expiration-date="formattedExpirationDate"
      />
    </div>
    <div class="game-entry-box"
      @click.prevent="$emit('details-clicked', entry)"
      :style="{
        'filter': (entry.disabled || isExpired) ? 'opacity(0.55)' : undefined,
      }"
    >
      <div
        class="game-box-title-container"
        :style="{
          'color': titleTextColor,
          'background-color': titleBackgroundColor,
        }"
      >
        <h5 class="game-box-title" v-html="entry.game.thumbnailTitle || entry.game.title"></h5>
      </div>
      <div class="game-image-container">
        <div v-if="entry.game.thumbnailImage || entry.game.titleImage"
          class="game-image"
          :style="`background-image: url('${resolveAssetPath(entry.game.thumbnailImage || entry.game.titleImage, 'ui/customer')}');`"
        ></div>
        <div v-else
          class="game-image"
          style="background-color: rgba(255, 255, 255, 0.5)"
        ></div>
      </div>
      <div
        class="description-preview-container"
        :style="{
          'color': detailsPreviewTextColor,
          'background-color': detailsPreviewBackgroundColor,
        }"
      >
        <div class="description-preview" v-html="entry.game.description"></div>
        <div class="description-preview-ellipse">...</div>
        <i v-if="entry.childGame || entry.complete"
          class="fas fa-check"
          style="position: absolute; bottom: 5px; left: 8px; font-size: 90%;"
          v-b-tooltip.hover
          title="Completed"
        ></i>
      </div>
    </div>
    <div class="button-container">
      <button
        v-if="entry.complete && !entry.inProgress"
        class="btn"
        disabled
      >
        Completed
      </button>
      <button
        v-else-if="!entry.disabled && !isExpired"
        class="btn"
        @click.prevent="$emit('play-clicked', entry)"
      >{{ entry.childGame ? resumeCompletedText : (entry.inProgress ? (entry.complete ? resumeCompletedText : 'Resume') : 'Start') }}</button>
      <button v-else class="btn" disabled>Locked</button>
      <button
        v-if="!entry.disabled && canRestartGame && (entry.inProgress || entry.complete) && !(entry.multiplayer)"
        class="btn-choice"
        @click.prevent="$emit('reset-clicked', entry)"
      >Restart</button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import GameExpirationBadge from './GameExpirationBadge';

export default {
  components: { GameExpirationBadge },
  props: {
    entry: { type: Object, required: true }
  },
  computed: {
    ...mapGetters([
      'canRestartGame',
      'activeEnvironment',
    ]),
    /** - activeEnvironment.hubBoxBackgroundColor, unless overridden
        - override or first fallback: game.thumbnailBackgroundColor
        - next fallback: activeEnvironment.primaryColorLightened (which falls back to primaryColor)
        - final fallback: hardcoded value */
    titleBackgroundColor() {
      const overridden =
        (this.entry && this.entry.game && this.entry.game.thumbnailBrandingOverride
          && this.entry.game.thumbnailBackgroundColor);
      if (!overridden && this.activeEnvironment
        && this.activeEnvironment.hubBoxBackgroundColor) {
        return this.activeEnvironment.hubBoxBackgroundColor;
      } else if (overridden
        || (this.entry && this.entry.game && this.entry.game.thumbnailBackgroundColor)) {
        return this.entry.game.thumbnailBackgroundColor;
      } else if (this.activeEnvironment
        && (this.activeEnvironment.primaryColorLightened || this.activeEnvironment.primaryColor)) {
        return this.activeEnvironment.primaryColorLightened || this.activeEnvironment.primaryColor;
      }
      return 'rgba(17, 17, 17, 0.5)';
    },
    /** - activeEnvironment.hubBoxTextColor, unless overridden
        - override or first fallback: game.thumbnailTextColor
        - final fallback: hardcoded value */
    titleTextColor() {
      const overridden =
        (this.entry && this.entry.game && this.entry.game.thumbnailBrandingOverride
          && this.entry.game.thumbnailTextColor);
      if (!overridden && this.activeEnvironment
        && this.activeEnvironment.hubBoxTextColor) {
        return this.activeEnvironment.hubBoxTextColor;
      } else if (overridden
        || (this.entry && this.entry.game && this.entry.game.thumbnailTextColor)) {
        return this.entry.game.thumbnailTextColor;
      }
      return 'white';
    },
    /** - activeEnvironment.hubDetailsPreviewTextColor, unless overridden
        - override or first fallback: game.detailsPreviewTextColor
        - final fallback: hardcoded value */
    detailsPreviewTextColor() {
      const overridden =
        (this.entry && this.entry.game && this.entry.game.thumbnailBrandingOverride
          && this.entry.game.detailsPreviewTextColor);
      if (!overridden && this.activeEnvironment
        && this.activeEnvironment.hubDetailsPreviewTextColor) {
        return this.activeEnvironment.hubDetailsPreviewTextColor;
      } else if (overridden
        || (this.entry && this.entry.game && this.entry.game.detailsPreviewTextColor)) {
        return this.entry.game.detailsPreviewTextColor;
      }
      return 'white';
    },
    /** - activeEnvironment.hubDetailsPreviewBackgroundColor, unless overridden
        - override or first fallback: game.detailsPreviewBackgroundColor
        - final fallback: hardcoded value */
    detailsPreviewBackgroundColor() {
      const overridden =
        (this.entry && this.entry.game && this.entry.game.thumbnailBrandingOverride
          && this.entry.game.detailsPreviewBackgroundColor);
      if (!overridden && this.activeEnvironment
        && this.activeEnvironment.hubDetailsPreviewBackgroundColor) {
        return this.activeEnvironment.hubDetailsPreviewBackgroundColor;
      } else if (overridden
        || (this.entry && this.entry.game && this.entry.game.detailsPreviewBackgroundColor)) {
        return this.entry.game.detailsPreviewBackgroundColor;
      }
      return 'rgba(17, 17, 17, 0.5)';
    },
    /** Temporary (purchased-via-catalog) content badges. The backend may
        annotate the flags on the entry or on the game; check both. */
    isTemporary() {
      const game = this.entry && this.entry.game;
      return Boolean(this.entry && (this.entry.temporary || (game && game.temporary)));
    },
    isExpired() {
      const game = this.entry && this.entry.game;
      return Boolean(this.entry && (this.entry.expired || (game && game.expired)));
    },
    formattedExpirationDate() {
      const game = this.entry && this.entry.game;
      const iso = (this.entry && this.entry.expirationDate) || (game && game.expirationDate);
      if (!iso) return null;
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString();
    },
    resumeCompletedText() {
      return this.entry.game.thumbnailResumeCompletedTextOverride || 'Resume (Completed)';
    },
  }
};
</script>

<style lang="scss" scoped>
.game-entry-root {
  position: relative;
  width: 320px;
  height: 450px;
  overflow: visible;

  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: flex-start;
  color: white;
}

.game-entry-box {
  width: 100%;
  height: 360px;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  border: 2px solid var(--primaryColor);
  cursor: pointer;
}

.game-box-title-container {
  width: 100%;
  height: 90px;
  display: flex;
  justify-content: center;
  flex-flow: column nowrap;
  background-color: rgba(17, 17, 17, 0.5);
  border-radius: 12px 12px 0px 0px;
  border-bottom: 2px solid white;
}

.game-box-temp-badge {
  position: absolute;
  top: -15px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 5;
  pointer-events: none;
}

.game-box-title {
  padding: 12px;
  margin: 0;
  width: 100%;
  text-align: center;
  vertical-align: middle;
  font-size: 125%;
}

.game-image-container {
  width: 100%;
  height: 180px;
}

.game-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position-x: center;
  background-position-y: center;
}

.description-preview-container {
  position: relative;
  width: 100%;
  height: 87px;
  padding: 4px 8px;
  background-color: rgba(17, 17, 17, 0.5);
  border-radius: 0 0 12px 12px;
  border-top: 2px solid var(--primaryColor);

  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
}

.description-preview {
  width: 100%;
  line-height: 19.7px;
  max-height: 59.1px;
  overflow: hidden;
}

.description-preview-ellipse {
  position: absolute;
  bottom: 5px;
  right: 7px;
}

.button-container {
  width: 100%;
  padding: 6px 0;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

.button-container button {
  width: 99%;
  margin-bottom: 9px;
  text-transform: uppercase;
  font-size: 130%;
}
</style>



// WEBPACK FOOTER //
// src/components/game/pages/components/GameHubGameBox.vue